// Live currency conversion. Rates are USD-based (fetched via the Worker's
// /api/fx, which edge-caches them) so any pair converts via cross-rates:
//   amount_to = amount_from * rate[to] / rate[from].
// Rates are cached in localStorage for a few hours so the UI stays responsive.

export interface FxRates {
  base: string; // always "USD"
  rates: Record<string, number>;
  date: string | null;
  fetchedAt: number;
}

const LS_KEY = "cg:fx";
const MAX_AGE = 6 * 3600 * 1000; // 6h

let mem: FxRates | null = null;

export async function getRates(force = false): Promise<FxRates | null> {
  const now = Date.now();
  if (!force && mem && now - mem.fetchedAt < MAX_AGE) return mem;
  if (!force) {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const cached = JSON.parse(raw) as FxRates;
        if (cached?.rates && now - cached.fetchedAt < MAX_AGE) { mem = cached; return cached; }
      }
    } catch {}
  }
  try {
    const r = await fetch("/api/fx");
    const j: any = await r.json();
    if (!j?.ok || !j.rates) return mem; // keep any stale copy
    const fx: FxRates = { base: "USD", rates: j.rates, date: j.date || null, fetchedAt: now };
    mem = fx;
    try { localStorage.setItem(LS_KEY, JSON.stringify(fx)); } catch {}
    return fx;
  } catch {
    return mem;
  }
}

/** Convert an amount between currencies using USD-based rates. Returns null when
 * a currency isn't in the table (caller should fall back to the raw amount). */
export function convert(amount: number, from: string, to: string, fx: FxRates | null): number | null {
  if (!isFinite(amount)) return null;
  const f = (from || "").toUpperCase().trim();
  const t = (to || "").toUpperCase().trim();
  if (!f || !t || f === t) return amount;
  if (!fx) return null;
  const rf = fx.rates[f], rt = fx.rates[t];
  if (!rf || !rt) return null;
  return amount * (rt / rf);
}
